/**
 * OpsEventBus — single shared SSE consumer for /api/ops/events.
 *
 * Replaces per-view EventSource instances (DedupView/OpsView used to
 * each open their own connection). Centralizes the rate-window for
 * sidebar's "ops-live" badge.
 *
 * Usage:
 *   import { opsBus } from './core/ops_bus.js';
 *   opsBus.start();
 *   const unsub = opsBus.subscribe(
 *     evt => evt.source === 'cron_dedup_match',
 *     evt => console.log('match event', evt)
 *   );
 *   // ... later:
 *   unsub();
 *
 * The bus is idempotent: multiple start() calls are no-ops once
 * connected. stop() is intended for logout / hot-reload only.
 */

const RATE_WINDOW_MS = 60_000;
const RECENT_EVENTS_CAP = 200;

class OpsEventBus {
  constructor() {
    this.es = null;
    this.subscribers = new Set(); // {filter, handler}
    this.connStateCbs = new Set(); // (state) => void
    this.connState = 'idle'; // idle | connecting | open | error
    this.timestamps = []; // ms epoch ints, for rolling rate
    this.recentEvents = []; // newest-first ring buffer (cap RECENT_EVENTS_CAP)
    // Late-mounting consumers (OpsView) seed
    // from this so the stream isn't empty when
    // first opened.
  }

  start() {
    if (this.es) return;
    this._setConnState('connecting');
    this.es = new EventSource('/api-zapd/events');
    this.es.addEventListener('open', () => this._setConnState('open'));
    this.es.addEventListener('error', () => this._setConnState('error'));
    // Note: EventSource auto-reconnects per server's `retry: 5000` directive.
    this.es.onmessage = (m) => {
      let evt;
      try {
        evt = JSON.parse(m.data);
      } catch (_) {
        return;
      }
      this._dispatch(evt);
    };
  }

  stop() {
    if (this.es) {
      this.es.close();
      this.es = null;
    }
    this.subscribers.clear();
    this.connStateCbs.clear();
    this.recentEvents.length = 0;
    this.timestamps.length = 0;
    this._setConnState('idle');
  }

  /**
   * @param {(evt:object)=>boolean} filter
   * @param {(evt:object)=>void} handler
   * @returns {()=>void} unsubscribe
   */
  subscribe(filter, handler) {
    const tup = { filter, handler };
    this.subscribers.add(tup);
    return () => this.subscribers.delete(tup);
  }

  /**
   * @param {(state:'idle'|'connecting'|'open'|'error')=>void} cb
   * @returns {()=>void} unsubscribe
   */
  onConnState(cb) {
    this.connStateCbs.add(cb);
    cb(this.connState);
    return () => this.connStateCbs.delete(cb);
  }

  /** Events/Minute über das letzte 60-s-Fenster. */
  getRatePerMinute() {
    const cutoff = Date.now() - RATE_WINDOW_MS;
    while (this.timestamps.length && this.timestamps[0] < cutoff) {
      this.timestamps.shift();
    }
    return this.timestamps.length;
  }

  /**
   * Snapshot der zuletzt gesehenen Events (newest-first, max RECENT_EVENTS_CAP).
   * Late-mounting Consumer (OpsView) seedet daraus, damit der Stream nicht
   * leer aussieht wenn man zum ersten Mal hinklickt — der Bus läuft schon
   * seit App-Bootstrap und hat in der Zwischenzeit Events gesammelt.
   */
  getRecentEvents() {
    return this.recentEvents.slice();
  }

  _dispatch(evt) {
    this.timestamps.push(Date.now());
    // Cap at ~10k entries to bound memory if window math ever fails
    if (this.timestamps.length > 10_000) this.timestamps.splice(0, 1000);
    // Ring-Buffer für late-mounting Consumer (OpsView).
    this.recentEvents.unshift(evt);
    if (this.recentEvents.length > RECENT_EVENTS_CAP) {
      this.recentEvents.length = RECENT_EVENTS_CAP;
    }
    for (const { filter, handler } of this.subscribers) {
      let matches = false;
      try {
        matches = filter(evt);
      } catch (_) {}
      if (matches) {
        try {
          handler(evt);
        } catch (e) {
          console.error('opsBus handler error', e);
        }
      }
    }
  }

  _setConnState(state) {
    this.connState = state;
    for (const cb of this.connStateCbs) {
      try {
        cb(state);
      } catch (_) {}
    }
  }
}

export const opsBus = new OpsEventBus();
